import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { TransactionRepository } from './repository';
import type { TransactionDetail, TransactionListQuery, TransactionSummary } from './types';
import { bangkokWallTimeToRfc3339, irreversibleKeyHint, toSafeFailure, toSafeTrace } from './types';

type ListResponse = ApiResponse<'/api/v1/transactions', 'get'>;
type DetailResponse = ApiResponse<'/api/v1/transactions/{id}', 'get'>;

export function createRealTransactionRepository(
  client: ApiClient,
  accessToken: string | null,
): TransactionRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const result = await client.get<ListResponse>('/api/v1/transactions', {
        ...auth,
        query: listQuery(query),
        signal,
      });
      return {
        items: result.data.data.map(toSummary),
        nextCursor: result.data.pagination.nextCursor,
        requestId: result.requestId,
      };
    },
    async get(transactionId, signal) {
      const result = await client.get<DetailResponse>(
        `/api/v1/transactions/${encodeURIComponent(transactionId)}`,
        { ...auth, signal },
      );
      const { failure, idempotency, requestMetadata, ...detail } = result.data.data;
      return {
        ...detail,
        failure: toSafeFailure(failure),
        idempotency: {
          id: idempotency.id,
          keyHint: irreversibleKeyHint(idempotency.key),
          state: idempotency.state,
          responseStatus: idempotency.responseStatus,
          createdAt: idempotency.createdAt,
          completedAt: idempotency.completedAt,
          expiresAt: idempotency.expiresAt,
        },
        requestId: result.requestId,
        requestTrace: toSafeTrace(requestMetadata),
      } satisfies TransactionDetail;
    },
  };
}

function toSummary(item: ListResponse['data'][number]): TransactionSummary {
  return { ...item, failure: toSafeFailure(item.failure) };
}

function listQuery(query: TransactionListQuery): Readonly<Record<string, unknown>> {
  const f = query.filters;
  return Object.fromEntries(
    Object.entries({
      cursor: query.cursor,
      limit: query.pageSize,
      sort: query.sort,
      partnerId: f.partnerId.trim(),
      agentId: f.agentId.trim(),
      memberId: f.memberId.trim(),
      username: f.username.trim(),
      partnerUserId: f.partnerUserId.trim(),
      uplineMemberId: f.uplineMemberId.trim(),
      uplineUsername: f.uplineUsername.trim(),
      gameTypeId: f.gameTypeId.trim(),
      gameTypeCode: f.gameTypeCode.trim().toUpperCase(),
      gameId: f.gameId.trim(),
      gameCode: f.gameCode.trim().toUpperCase(),
      gameRound: f.gameRound.trim(),
      partnerTransactionId: f.partnerTransactionId.trim(),
      outcome: f.outcome,
      status: f.status,
      reversalState: f.reversalState,
      currency: f.currency.trim().toUpperCase(),
      errorCode: f.errorCode.trim().toUpperCase(),
      minimumAmount: f.minimumAmount.trim(),
      maximumAmount: f.maximumAmount.trim(),
      playFrom: bangkokWallTimeToRfc3339(f.playFrom),
      playTo: bangkokWallTimeToRfc3339(f.playTo),
      receivedFrom: bangkokWallTimeToRfc3339(f.receivedFrom),
      receivedTo: bangkokWallTimeToRfc3339(f.receivedTo),
    }).filter(([, value]) => value !== ''),
  );
}
